import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import { invalidateClaimsQueries } from '@/admin/hooks/use-claims'

/**
 * Member-profile custom attributes.
 *
 * Tenants define extra EAV fields on the tenant-scoped ``Membership`` join
 * (never on the locked Tier 3 ``user`` entity). The rich member detail lives
 * under ``/members/{uuid}/`` and per-member values under
 * ``/members/{uuid}/attribute-values/``. The backend wraps every payload as
 * ``{ status, message, result }`` (see common/response.py), so we read
 * ``r.data.result ?? r.data`` throughout.
 */

/** One row of a member's ``attribute_values`` payload: schema + current value. */
export interface MemberAttributeValue {
  code_name: string
  display_name: string
  description: string
  field_type: string
  configuration_data: Record<string, unknown>
  required: boolean
  editable: boolean
  order: number
  value: unknown
  // Null for a tenant-wide field; the role's name when the field is defined on
  // a role the member holds, so the profile can group fields by their role.
  applies_to_role_name?: string | null
}

export interface MemberProfileRole {
  uuid: string
  name: string
  is_admin: boolean
  is_primary: boolean
}

/** The rich member-detail payload from ``GET .../members/{uuid}/``. */
export interface MemberProfile {
  uuid: string
  user_uuid: string
  user_email: string
  user_name: string
  user_first_name: string
  user_last_name: string
  role_uuid: string
  role_name: string
  roles: MemberProfileRole[]
  is_owner: boolean
  inspector_type: string | null
  last_login: string | null
  date_joined: string | null
  member_since: string | null
  attribute_values: MemberAttributeValue[]
}

// ── Member profile (detail) ─────────────────────────────────

export function useMemberProfile(membershipUuid: string | undefined) {
  const { orgUuid } = useOrg()
  return useQuery<MemberProfile>({
    queryKey: ['member', orgUuid, membershipUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/members/${membershipUuid}/`)
        .then((r) => (r.data.result ?? r.data) as MemberProfile),
    enabled: !!orgUuid && !!membershipUuid,
    staleTime: 30_000,
  })
}

// ── Open claims a zone hand-over moves (WPS-160) ────────────

/** One open claim that would move to the member. */
export interface ClaimMoveClaim {
  uuid: string
  number: string
  state_key: string
  state_label: string
}

/** One (zone, template) pair a save gives the member, with its open claims. */
export interface ClaimMovePair {
  zone: { uuid: string; name: string; code: string }
  channel: { key: string; label: string }
  product_template: { uuid: string; name: string } | null
  previous_owners: { user_uuid: string; name: string }[]
  count: number
  claims: ClaimMoveClaim[]
}

/** ``?preview=1`` on a member save: the open claims it would move to them. */
export interface ClaimMovePreview {
  count: number
  pairs: ClaimMovePair[]
}

const membersPath = (orgUuid: string | null | undefined, membershipUuid?: string) =>
  `/organisations/${orgUuid}/members/${membershipUuid}`

/**
 * PATCH .../attribute-values/?preview=1 - validate a profile save and list
 * the open claims it would move to the member. Writes nothing; a rejected
 * save comes back as the usual per-field errors.
 */
export function usePreviewMemberAttributeValues(
  membershipUuid: string | undefined,
) {
  const { orgUuid } = useOrg()
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api
        .patch(`${membersPath(orgUuid, membershipUuid)}/attribute-values/`, payload, {
          params: { preview: 1 },
        })
        .then((r) => (r.data.result ?? r.data) as ClaimMovePreview),
  })
}

/**
 * PATCH .../members/{uuid}/attribute-values/ - partial ``code_name -> value``
 * map. With ``reassignOpenClaims`` the open claims the preview listed move to
 * the member in the same save. Returns the refreshed ``attribute_values`` and
 * how many claims moved.
 */
export function useUpdateMemberAttributeValues(
  membershipUuid: string | undefined,
) {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      values,
      reassignOpenClaims = false,
    }: {
      values: Record<string, unknown>
      reassignOpenClaims?: boolean
    }) =>
      api
        .patch(`${membersPath(orgUuid, membershipUuid)}/attribute-values/`, values, {
          params: reassignOpenClaims ? { reassign_open_claims: 1 } : undefined,
        })
        .then((r) => {
          // With the flag the result also carries how many claims moved.
          const body = r.data.result ?? r.data
          return reassignOpenClaims
            ? {
                attributeValues: body.attribute_values as MemberAttributeValue[],
                reassignedClaims: Number(body.reassigned_claims ?? 0),
              }
            : { attributeValues: body as MemberAttributeValue[], reassignedClaims: 0 }
        }),
    onSuccess: ({ reassignedClaims }) => {
      queryClient.invalidateQueries({
        queryKey: ['member', orgUuid, membershipUuid],
      })
      // Zone fields on the profile are zone ownership too.
      queryClient.invalidateQueries({ queryKey: ['geo-assignments'] })
      if (reassignedClaims > 0) invalidateClaimsQueries(queryClient)
    },
  })
}
